<template>
    <div>
        <v-form :disabled="disabled">
            <v-col cols="12" xl="10" class="px-0">
                <b-alert
                    :message="t('default::alert.info.title')"
                    has-content
                    expanded
                    :list="[
                        t('dms-users::permissions.info'),
                        t('dms-users::permissions.info.group_only'),
                        t('dms-users::permissions.info.sites_only'),
                        t('dms-users::permissions.info.sites_and_group'),
                        t('dms-users::permissions.info.no_site_no_group'),
                        t('dms-users::permissions.info.disabled_routes'),
                    ]"
                >
                </b-alert>
            </v-col>
            <v-col cols="12" xl="10" class="px-0">
                <v-row>
                    <v-col v-if="has_all_sites_and_groups_deleted" cols="12" class="py-0">
                        <b-alert type="error" :message="t('dms-users::form.status.no_permissions.info')"></b-alert>
                    </v-col>

                    <v-col cols="12" lg="6" class="py-0">
                        <roles-selector
                            v-model="dmsUserRoles"
                            groups="admins,hcps"
                            :error-messages="errorMessageRoles"
                            :label="t('users::create.role')"
                            :rules="[(v) => !!v || t('users::validation.role.required')]"
                            background-color="#fff"
                            outlined
                            :clearable="!readOnlyRules"
                            :readonly="readOnlyRules"
                            component-name="v-autocomplete"
                            @change="extractActiveRoutes"
                        >
                        </roles-selector>
                    </v-col>
                    <v-col cols="12" lg="6" class="py-0">
                        <b-select
                            to-mutate="dms_users::dms_user.pharmacy_group_id"
                            :label="t('dms-users::form.label.pharmacy_group')"
                            :select-items="pharmacyGroupsOptions"
                            item-value="id"
                            item-text="name"
                            clearable
                            :readonly="readOnlyRules || canEditPharmacy"
                            component-name="v-autocomplete"
                        ></b-select>
                    </v-col>
                    <v-col cols="12" lg="6" class="pt-0">
                        <b-select
                            to-mutate="dms_users::dms_user.pharmacy_sites"
                            :label="t('dms-users::form.label.pharmacy_sites')"
                            multiple
                            :select-items="pharmacySitesOptions"
                            item-value="id"
                            item-text="name"
                            :readonly="readOnlyRules || canEditPharmacy"
                            component-name="v-autocomplete"
                            :clearable="false"
                        >
                            <template #item="{ item, on, attrs }">
                                <v-list-item v-slot="{ active }" v-bind="attrs" v-on="on">
                                    <v-list-item-action>
                                        <v-checkbox :ripple="false" :input-value="active"></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            {{ item.name }}
                                            <span v-if="!item.is_registered">&nbsp;[DEREGISTERED]</span>
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                            <template #selection="{ item, on, attrs }">
                                <v-chip v-bind="attrs" v-on="on">
                                    {{ item.name }}
                                    <span v-if="!item.is_registered">&nbsp;[DEREGISTERED]</span>
                                </v-chip>
                            </template>
                        </b-select>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12 p-0 px-2" xl="10">
                <v-card flat>
                    <v-expansion-panels v-if="!loading && routes" class="pb-4">
                        <v-expansion-panel v-for="(item, key) in routes" v-show="isRoleActiveGroup(key)" :key="key">
                            <v-expansion-panel-header>{{ key }}</v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-row no-gutters>
                                    <v-col
                                        v-for="route in item"
                                        v-show="isAllowedRoleRoute(route.id)"
                                        :key="route.id"
                                        cols="12"
                                        lg="3"
                                        md="6"
                                        sm="12"
                                    >
                                        <v-checkbox
                                            v-model="permissions"
                                            :label="route.name"
                                            :value="route.id"
                                            color="primary"
                                            dense
                                            hide-details
                                            size="100%"
                                            @change="changePermission($event, route.id)"
                                        >
                                        </v-checkbox>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-card>
            </v-col>
        </v-form>
    </div>
</template>

<script>
export default {
    props: {
        id: {
            type: String,
            required: false,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            adminDmsStaffRoleId: null,
            roleActiveRoutesIds: [],
            rolesLoaded: false,
            isFormValid: false,
            routes: {},
            roles: null,
            pharmacySitesOptionsModified: false,
            user: {
                roles: [],
                disabled_permissions: [],
            },
            errorMessages: {
                roles: [],
            },
            accessInfo: [],
            readOnlyRules:
                !(this.can('api.v1.dms-users.update') || this.can('api.v1.dms-users.store')) &&
                this.can('api.v1.dms-users.show'),
        };
    },
    computed: {
        dmsUserRoles: {
            get() {
                return (
                    _.get(this.$store.state.dms_users, 'dms_user.roles')[0].id ||
                    _.get(this.$store.state.dms_users, 'dms_user.roles')[0]
                );
            },
            set(value) {
                this.$store.dispatch('dms_users/updateProperty', {
                    value: [{ id: value }],
                    property: 'dms_user.roles',
                });
            },
        },
        disabled_permissions: {
            get() {
                return _.get(this.$store.state.dms_users, 'dms_user.disabled_permissions');
            },
            set(value) {
                this.$store.dispatch('dms_users/updateProperty', { value, property: 'dms_user.disabled_permissions' });
            },
        },
        has_all_sites_and_groups_deleted() {
            return _.get(this.$store.state.dms_users, 'dms_user.has_all_sites_and_groups_deleted');
        },
        permissions: {
            get() {
                return this.roleActiveRoutesIds.filter((routeId) => {
                    return !this.disabled_permissions.includes(routeId);
                });
            },
            set() {
                //nothing here
            },
        },
        canEditPharmacy() {
            const rolesIds = this.$store.state.dms_users.dms_user.roles.map((role) => role.id);
            const isAdminDmsStaffSelected = rolesIds.includes(this.adminDmsStaffRoleId);
            if (isAdminDmsStaffSelected) this.clearPharmacyFields();
            return isAdminDmsStaffSelected;
        },
        loading() {
            return this.$store.state.dms_users.loading;
        },
        pharmacySitesOptions() {
            return this.$store.state.dms_users.pharmacySitesOptions;
        },
        pharmacyGroupsOptions() {
            return this.$store.state.dms_users.pharmacyGroupsOptions;
        },
        errorMessageRoles() {
            return this.$store.state.dms_users.errorMessages.dms_user.roles;
        },
    },
    created() {
        this.can('api.v1.acl-routes.grouped') ? this.loadRoutes() : false;
    },
    methods: {
        clearPharmacyFields() {
            const pharmacy_site = {
                property: 'dms_user.pharmacy_sites',
                value: [],
            };
            const pharmacy_group = {
                property: 'dms_user.pharmacy_group_id',
                value: null,
            };
            this.$store.dispatch('dms_users/updateProperty', pharmacy_site);
            this.$store.dispatch('dms_users/updateProperty', pharmacy_group);
        },
        // onChangePharmacyGroup(pharmacyGroup) {
        //     console.warn('pharmacyGroup', pharmacyGroup);
        //     this.$store.dispatch('dms_users/loadPharmacySitesOptions');
        // },
        getSelectedRoles() {
            if (!this.dmsUserRoles) {
                return [];
            }

            if (Array.isArray(this.dmsUserRoles)) {
                return this.dmsUserRoles;
            }

            return [this.dmsUserRoles];
        },
        sendRecoveryPasswordMail(redirect = false) {
            axios
                .post(route('api.v1.auth.reset-password'), {
                    email: this.user.email,
                    is_new: true,
                })
                .then(() => {
                    this.$root.notify('info', t('login::password.link.sent.message'));

                    if (redirect) {
                        this.$root.goTo(route('users.index'));
                    }
                })
                .catch((error) => {
                    this.$root.parseErrorsAndNotify(error);
                });
        },
        loadRoutes() {
            axios
                .get(route('api.v1.acl-routes.grouped'), {
                    params: {
                        allowed_for_guests: 0,
                        allowed_for_all: 0,
                        limit: -1,
                    },
                })
                .then((response) => {
                    this.routes = response.data.data;
                    this.loadRoles();
                });
        },
        loadRoles() {
            axios
                .get(
                    route('api.v1.roles.index', {
                        fields: 'id,name,acl_routes,description,reset_password,slug',
                        limit: -1,
                    })
                )
                .then((response) => {
                    this.roles = response.data.data;
                    this.adminDmsStaffRoleId = this.roles.find((x) => x.slug === 'admin-dms-staff')['id'] || null;
                    this.rolesLoaded = true;
                    this.extractActiveRoutes();
                });
        },
        extractActiveRoutes() {
            this.$store.dispatch('dms_users/setErrorMessage', {
                value: [],
                property: 'dms_user.roles',
            });
            this.roleActiveRoutesIds = [];

            _.forEach(this.roles, (role) => {
                const selectedRoles = this.getSelectedRoles();

                if (_.includes(selectedRoles, role.id)) {
                    this.roleActiveRoutesIds = _.concat(
                        this.roleActiveRoutesIds,
                        role.acl_routes.map((route) => route.id)
                    );
                }
            });

            this.roleActiveRoutesIds = _.uniq(this.roleActiveRoutesIds);
        },
        isAllowedRoleRoute(routeId) {
            return this.roleActiveRoutesIds.includes(routeId);
        },
        isRoleActiveGroup(group) {
            return (
                _.filter(this.routes[group], (route) => {
                    return this.roleActiveRoutesIds.includes(route.id);
                }).length > 1
            );
        },
        changePermission(all, routeId) {
            this.$store.dispatch('dms_users/changePermissions', { all, routeId });
        },
    },
};
</script>

<style></style>
